import { useMemo, useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Stepper,
  Text,
  TextInput,
  Grid,
  Table,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DatePickerInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useBranches, useDepartments } from '../../hooks/useCompany';
import { useCreateEmployee, useEmployees } from '../../hooks/useEmployees';
import type { CreateEmployeeInput } from '../../types/employee';

export default function EmployeeCreatePage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const createEmployee = useCreateEmployee();
  const { data: deptData } = useDepartments();
  const { data: branchData } = useBranches();
  const { data: empData } = useEmployees({ perPage: 200, status: 'active' });

  const depts = deptData as { departments?: Array<{ id: string; name: string }> } | undefined;
  const branches = branchData as { branches?: Array<{ id: string; name: string }> } | undefined;
  const emps = empData as { employees?: { data?: Array<{ id: string; firstName: string; lastName: string }> } } | undefined;

  const departmentOptions = useMemo(
    () => (depts?.departments ?? []).map((d) => ({ value: d.id, label: d.name })),
    [depts],
  );
  const branchOptions = useMemo(
    () => (branches?.branches ?? []).map((b) => ({ value: b.id, label: b.name })),
    [branches],
  );
  const managerOptions = useMemo(
    () => (emps?.employees?.data ?? []).map((e) => ({
      value: e.id,
      label: `${e.firstName} ${e.lastName}`,
    })),
    [emps],
  );

  const form = useForm<CreateEmployeeInput>({
    initialValues: {
      firstName: '',
      lastName: '',
      middleName: '',
      email: '',
      phone: '',
      hireDate: '',
      departmentId: '',
      branchId: '',
      jobTitle: '',
      managerId: '',
      payType: 'hourly',
      payRate: 0,
      payFrequency: 'biweekly',
      filingStatus: 'single',
      allowances: 0,
    },
    validate: {
      firstName: (v) => (v.trim().length < 1 ? 'First name is required' : null),
      lastName: (v) => (v.trim().length < 1 ? 'Last name is required' : null),
      email: (v) => {
        if (!v) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(v)) return 'Invalid email';
        return null;
      },
      hireDate: (v) => (!v ? 'Hire date is required' : null),
      departmentId: (v) => (!v ? 'Department is required' : null),
      branchId: (v) => (!v ? 'Branch is required' : null),
      jobTitle: (v) => (v.trim().length < 1 ? 'Job title is required' : null),
      payRate: (v) => (v <= 0 ? 'Pay rate must be greater than 0' : null),
    },
  });

  const validateStep = (step: number): boolean => {
    const fieldsByStep: Record<number, (keyof CreateEmployeeInput)[]> = {
      0: ['firstName', 'lastName', 'email'],
      1: ['hireDate', 'departmentId', 'branchId', 'jobTitle'],
      2: ['payRate'],
    };
    const fields = fieldsByStep[step] ?? [];
    let valid = true;
    for (const field of fields) {
      const result = form.validateField(field);
      if (result.hasError) valid = false;
    }
    return valid;
  };

  const nextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep((s) => Math.min(s + 1, 3));
    }
  };

  const prevStep = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    try {
      const employee = await createEmployee.mutateAsync(form.values);
      notifications.show({
        title: 'Employee Created',
        message: `${employee.firstName} ${employee.lastName} has been added.`,
        color: 'green',
      });
      navigate(`/employees/${employee.id}`);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create employee',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="New Employee"
        breadcrumbs={[
          { label: 'Employees', to: '/admin/workforce/employees' },
          { label: 'New Employee' },
        ]}
      />

      {/* Smart Onboarding CTA */}
      <Paper withBorder p="md" radius="md" bg="var(--mantine-color-blue-0)">
        <Group justify="space-between" ta="center">
          <div>
            <Text fw={600} size="sm">Have the employee&apos;s ID and SSN card?</Text>
            <Text size="xs" c="dimmed">
              Use Smart Onboarding to auto-fill this form from a photo. AI extracts name, address, SSN,
              and pre-fills W-4 and I-9 forms automatically.
            </Text>
          </div>
          <Button
            variant="filled"
            color="blue"
            onClick={() => navigate('/admin/workforce/onboarding/smart')}
          >
            Smart Onboard
          </Button>
        </Group>
      </Paper>

      <Paper withBorder p="xl" radius="md">
        <Stepper active={activeStep} onStepClick={setActiveStep}>
          <Stepper.Step label="Personal Info" description="Basic information">
            <Grid mt="md">
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="First Name"
                  placeholder="John"
                  required
                  {...form.getInputProps('firstName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Middle Name"
                  placeholder="Michael"
                  {...form.getInputProps('middleName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Last Name"
                  placeholder="Doe"
                  required
                  {...form.getInputProps('lastName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Email"
                  placeholder="john.doe@company.com"
                  required
                  {...form.getInputProps('email')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Phone"
                  placeholder="(555) 123-4567"
                  {...form.getInputProps('phone')}
                />
              </Grid.Col>
            </Grid>
          </Stepper.Step>

          <Stepper.Step label="Employment" description="Job details">
            <Grid mt="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <DatePickerInput
                  label="Hire Date"
                  placeholder="Select date"
                  required
                  value={form.values.hireDate ? new Date(form.values.hireDate) : null}
                  onChange={(date) =>
                    form.setFieldValue(
                      'hireDate',
                      date ? new Date(date as string | Date).toISOString().split('T')[0] ?? '' : '',
                    )
                  }
                  error={form.errors.hireDate}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Job Title"
                  placeholder="Software Engineer"
                  required
                  {...form.getInputProps('jobTitle')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Department"
                  placeholder="Select department"
                  required
                  {...form.getInputProps('departmentId')}
                  data={departmentOptions}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Branch"
                  placeholder="Select branch"
                  required
                  {...form.getInputProps('branchId')}
                  data={branchOptions}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Manager"
                  placeholder="Select manager (optional)"
                  clearable
                  searchable
                  {...form.getInputProps('managerId')}
                  data={managerOptions}
                />
              </Grid.Col>
            </Grid>
          </Stepper.Step>

          <Stepper.Step label="Compensation" description="Pay details">
            <Grid mt="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Pay Type"
                  required
                  {...form.getInputProps('payType')}
                  data={[
                    { value: 'salary', label: 'Salary' },
                    { value: 'hourly', label: 'Hourly' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={form.values.payType === 'salary' ? 'Annual Salary' : 'Hourly Rate'}
                  placeholder="0.00"
                  required
                  prefix="$"
                  decimalScale={2}
                  thousandSeparator=","
                  min={0}
                  {...form.getInputProps('payRate')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Pay Frequency"
                  required
                  {...form.getInputProps('payFrequency')}
                  data={[
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'biweekly', label: 'Bi-Weekly' },
                    { value: 'semimonthly', label: 'Semi-Monthly' },
                    { value: 'monthly', label: 'Monthly' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Filing Status"
                  required
                  {...form.getInputProps('filingStatus')}
                  data={[
                    { value: 'single', label: 'Single' },
                    { value: 'married_joint', label: 'Married Filing Jointly' },
                    { value: 'married_separate', label: 'Married Filing Separately' },
                    { value: 'head_of_household', label: 'Head of Household' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Allowances"
                  min={0}
                  max={10}
                  {...form.getInputProps('allowances')}
                />
              </Grid.Col>
            </Grid>
          </Stepper.Step>

          <Stepper.Completed>
            <Stack gap="md" mt="md">
              <Text fw={600} size="lg">
                Review Employee Information
              </Text>
              <Paper withBorder p="md">
                <Table>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td fw={600} w={200}>Name</Table.Td>
                      <Table.Td>
                        {form.values.firstName} {form.values.middleName} {form.values.lastName}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Email</Table.Td>
                      <Table.Td>{form.values.email}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Phone</Table.Td>
                      <Table.Td>{form.values.phone || 'Not provided'}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Hire Date</Table.Td>
                      <Table.Td>{form.values.hireDate}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Job Title</Table.Td>
                      <Table.Td>{form.values.jobTitle}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Pay Type</Table.Td>
                      <Table.Td>{form.values.payType}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Pay Rate</Table.Td>
                      <Table.Td>${form.values.payRate.toLocaleString()}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Pay Frequency</Table.Td>
                      <Table.Td>{form.values.payFrequency}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Filing Status</Table.Td>
                      <Table.Td>{form.values.filingStatus}</Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          </Stepper.Completed>
        </Stepper>

        <Group justify="space-between" mt="xl">
          <Button variant="default" onClick={prevStep} disabled={activeStep === 0}>
            Back
          </Button>
          {activeStep < 3 ? (
            <Button onClick={nextStep}>Next</Button>
          ) : (
            <Button
              color="green"
              onClick={handleSubmit}
              loading={createEmployee.isPending}
            >
              Create Employee
            </Button>
          )}
        </Group>
      </Paper>
    </Stack>
  );
}
